import * as R from "ramda";

import { objFromListWith } from "../../../../utils/arrays";
import i18n from "../../../../i18n";
import { DEFAULT_PROCESS_STEPS } from "../../../../constants/opportunity";

// Normaliza la decisión de Equifax soporte ambas estructuras (legacy 'grupoCampos.campo[0].valor' + nuevo formato 'grupoCampos[0].valor'), evitando inconsistencias entre tarjeta y filtro.
const getEquifaxDecision = serviceCallResultData => {
  const legacyDecision = R.pathOr(
    "",
    ["grupoCampos", "campo", 0, "valor"],
    serviceCallResultData
  );
  if (legacyDecision) return legacyDecision;

  const groups = R.pathOr([], ["grupoCampos"], serviceCallResultData);
  if (!Array.isArray(groups)) return "";

  return (
    R.pathOr("", [0, "valor"], groups) ||
    R.pathOr("", [0, "campo", 0, "valor"], groups)
  );
};

const getEquifaxTag = decision => {
  if (decision === "PROCEDE") return "proceed";
  if (!decision) return undefined;
  return "rejected";
};

export const normalizeCandidatesForRequestedStep = (
  requestedStepId,
  candidates = []
) =>
  R.map(candidate =>
    R.isNil(R.prop("step", candidate))
      ? R.assoc("step", requestedStepId, candidate)
      : candidate
  )(candidates);

export const getCandidatesForStep = (requestedStepId, candidates = []) =>
  R.pipe(
    normalizeCandidatesForRequestedStep.bind(null, requestedStepId),
    R.filter(R.propEq("step", requestedStepId))
  )(candidates);

export const hasEmptyRooms = step => {
  if (!step) return false;

  const rooms = R.propOr([], "rooms", step);

  if (!R.propEq("type", "group-interview", step)) return true;
  if (!R.length(rooms)) return false;

  return R.any(
    ({ candidates }) =>
      R.length(candidates) < R.pathOr(0, ["config", "maxCandidates"], step)
  )(rooms);
};

export const hasEmptyMap = step => {
  if (!step) return false;

  return (
    R.propEq("type", "chatbot", step) &&
    !R.hasPath(["config", "chatTree"], step)
  );
};

export const isLastOfferPending = R.pipe(
  R.propOr([], "offerletter"),
  R.sort(R.descend(R.prop("createdAt"))),
  R.head,
  R.ifElse(R.isNil, R.F, R.propEq("status", "sent"))
);

export const isChatbotCompleted = (step, match) => {
  if (R.prop("type", step) !== "chatbot") return true;
  const chatbotStatus = getChatbotStatus(step, match);
  return chatbotStatus && chatbotStatus !== "PENDING";
};

export const hasTest = (step, match) => {
  if (step.type !== "test-gh" && step.type !== "test") return true;

  const { source: sourceTest, idTest } = step.config;
  const matchTest = R.pipe(
    m => [...R.pathOr([], ["humanTests"], m), ...R.pathOr([], ["tests"], m)],
    R.find(
      ({ source, testIdentifier, serviceCallResult }) =>
        (source || "gh") === (sourceTest || "gh") &&
        Number(testIdentifier) === Number(idTest) &&
        serviceCallResult === "success"
    )
  )(match);

  return !!matchTest;
};

export const getChatbotStatus = (step, match) =>
  step.type === "chatbot" &&
  R.pipe(
    R.propOr([], "chatbotFinished"),
    R.find(R.propEq("idStep", step.id)),
    R.defaultTo({}),
    R.propOr("PENDING", "chatbotStatus")
  )(match);

export const parseSteps = (steps, matches) => {
  const matchesValues = R.values(matches);

  return R.pipe(
    // Iteramos cada step
    R.map(step =>
      // Agregamos una prop matches a cada step
      R.assoc(
        "matches",
        R.pipe(
          R.propOr([], "columnOrder"),
          // Reemplazamos los ids que tenemos en columnOrder por el objeto real del match
          R.map(id => R.prop(id, matches)),
          // Solo nos quedamos con los que pertenezcan a este step
          R.filter(R.unless(R.isNil, R.propEq("step", step.id))),
          // Concatenamos todos los matches que no hayan estado en columnOrder pero que sean del step actual
          foundMatches =>
            R.concat(
              foundMatches,
              R.symmetricDifference(
                R.filter(R.propEq("step", step.id), matchesValues),
                foundMatches
              )
            ),
          // En caso de que sea el primer step, agregamos todos los matches que no tengan un step definido
          R.when(
            R.always(R.propEq("order", 0, step)),
            R.concat(R.__, R.reject(R.has("step"), matchesValues))
          ),
          // En caso de moco sacamos los repetidos
          R.uniqBy(R.prop("id")),
          // Ordenamos los matches por columnOrder en caso de alguna inconsistencia
          R.sortBy(match => {
            const index = R.findIndex(
              R.equals(match.id),
              step.columnOrder || []
            );
            return index === -1 ? Number.MAX_SAFE_INTEGER : index;
          })
        )(step)
      )(step)
    ),
    // Convertimos el array en un objeto con el id del step como key
    objFromListWith(R.prop("id"))
  )(steps);
};

export const parseMatches = objFromListWith(R.prop("id"));

export const getStepDisplayName = (step, t) => {
  if (!step) return "";
  if (typeof step.displayName === "string" && step.displayName.trim()) {
    return step.displayName.trim();
  }

  return step.default
    ? t(`opportunity:process.defaultSteps.${step.name}`, step.name)
    : step.name;
};

export const formatStep = step =>
  R.pipe(
    R.assoc(
      "columnOrder",
      R.pipe(
        R.prop("matches"),
        R.pluck("id")
      )(step)
    )
  )(step);

export const getStepsAfterMove = (
  { sourceStep, destinationStep, destinationMatchIndex, matchId },
  state
) =>
  R.pipe(
    R.evolve({
      [sourceStep]: {
        matches: R.reject(R.propEq("id", matchId))
      }
    }),
    R.evolve({
      [destinationStep]: {
        matches: R.insert(
          destinationMatchIndex,
          R.pipe(
            R.prop(matchId),
            R.assoc("step", destinationStep)
          )(state.matches)
        )
      }
    })
  )(state.steps);

export const isOpportunityFull = (step, opportunity, count = 1) =>
  R.propEq("name", DEFAULT_PROCESS_STEPS.accepted, step) &&
  opportunity.positions - step.matches.length < count;

export const canMoveToStepWarning = ({
  step,
  nextStep,
  matches,
  opportunity
}) => {
  if (
    nextStep &&
    opportunity &&
    isOpportunityFull(nextStep, opportunity, matches ? matches.length : 1)
  )
    return i18n.t(
      "opportunity:candidates.multipleSelection.acceptedStepIsFull"
    );

  if (nextStep && hasEmptyMap(nextStep))
    return i18n.t("opportunity:process.stepTypes.chatbot.invalidChatbotMap");

  if (nextStep && !hasEmptyRooms(nextStep))
    return i18n.t("opportunity:process.rooms.noEmpty");

  // if (step && matches && ["test-gh", "test"].includes(step.type)) {
  //   const error = matches.some(match => !hasTest(step, match));
  //   if (error)
  //     return i18n.t("opportunity:process.stepTypes.test.cannotMoveMessage");
  // }

  if (step && matches && step.type === "chatbot") {
    const error = matches.some(match => !isChatbotCompleted(step, match));
    if (error)
      return i18n.t(
        "opportunity:process.stepTypes.chatbot.chatbotConversationNotFinished"
      );
  }

  if (matches) {
    const error = matches.some(match => isLastOfferPending(match));
    if (error)
      return i18n.t("opportunity:process.candidateDetails.offer.notAccepted");
  }
};

export const getAvailableSteps = (stepIndex, stepsOrder, steps) =>
  R.pipe(
    R.slice(stepIndex + 1, stepsOrder.length),
    R.map(R.prop(R.__, steps))
  )(stepsOrder);

export const addUnreadMessages = (opportunity, channels, state) =>
  R.evolve({
    steps: R.map(
      R.evolve({
        matches: R.map(match =>
          R.assoc(
            "unreadMessages",
            R.pipe(
              R.pathOr([], [opportunity.id, match.id, "messages"]),
              R.filter(
                R.allPass([
                  R.propEq("source", "candidate"),
                  R.propSatisfies(R.not, "viewed")
                ])
              ),
              R.length
            )(channels),
            match
          )
        )
      })
    )
  })(state);

export const getName = candidate =>
  `${candidate.fullname || `${candidate.first} ${candidate.last}`}`
    .split(" ")
    .join("");

export const isPendingStep = ({ step, match }) => {
  return R.anyPass([
    R.both(
      R.either(
        R.pathEq(["step", "type"], "test-gh"),
        R.pathEq(["step", "type"], "test")
      ),
      ({ step, match }) => !hasTest(step, match)
    ),
    ({ step, match }) =>
      R.prop("type", step) === "chatbot" && !isChatbotCompleted(step, match),
    ({ match }) => isLastOfferPending(match)
  ])({ step, match });
};

export const defaultMatchesFilters = {
  type: [],
  status: [],
  successProbabilityTags: [],
  rooms: [],
  chatbotTags: [],
  tags: [],
  equifaxTags: [],
  searchText: ""
};

export function filterMatches(
  step,
  filters = defaultMatchesFilters,
  matches,
  successProbabilityProfile
) {
  return R.pipe(
    R.when(
      R.always(
        R.either(
          R.pathEq(["step", "type"], "test-gh"),
          R.pathEq(["step", "type"], "test")
        )(step)
      ),
      R.sortBy(match => (hasTest(step, match) ? -1 : 1))
    ),
    R.when(
      R.always(filters.type.length > 0),
      R.filter(match =>
        filters.type.includes(
          R.propOr(true, "isRegistered", match) ? "internal" : "external"
        )
      )
    ),
    R.when(
      R.always(filters.status.length > 0),
      R.filter(match =>
        filters.status.includes(
          isPendingStep({ step, match }) ? "with-pending" : "without-pending"
        )
      )
    ),
    R.when(
      R.always(filters.successProbabilityTags.length > 0),
      R.filter(match =>
        R.pipe(
          R.propOr([], "tests"),
          R.find(
            R.both(
              R.propEq("source", "worcket"),
              R.propEq("testIdentifier", "3")
            )
          ),
          R.propOr([], "serviceCallResultData"),
          R.find(R.propEq("idProfile", successProbabilityProfile || 0)),
          R.prop("tag"),
          tag => filters.successProbabilityTags.includes(tag)
        )(match)
      )
    ),
    R.when(
      R.always(filters.equifaxTags.length > 0),
      R.filter(match =>
        R.pipe(
          R.propOr([], "tests"),
          R.find(
            R.both(
              R.propEq("source", "worcket"),
              R.propEq("testIdentifier", "7")
            )
          ),
          R.propOr({}, "serviceCallResultData"),
          getEquifaxDecision,
          getEquifaxTag,
          tag => filters.equifaxTags.includes(tag)
        )(match)
      )
    ),
    R.when(
      R.always(filters.rooms.length > 0),
      R.filter(match =>
        R.pipe(
          R.prop("id"),
          R.pipe(
            R.filter(r => R.includes(r.name, filters.rooms)),
            R.pluck("candidates"),
            R.flatten,
            R.flip(R.includes)
          )(step.rooms)
        )(match)
      )
    ),
    R.when(
      R.always(filters.chatbotTags.length > 0),
      R.filter(match =>
        R.pipe(
          R.propOr([], "chatbotTags"),
          R.filter(R.propEq("idStep", step.id)),
          R.pluck("chatbotTags"),
          R.flatten,
          chatbotTags =>
            filters.chatbotTags.some(tag => chatbotTags.includes(tag))
        )(match)
      )
    ),
    R.when(
      R.always(filters.tags.length > 0),
      R.filter(match =>
        R.pipe(
          R.pathOr([], ["tags", step.idOpportunity]),
          tags => filters.tags.some(tag => tags.includes(tag))
        )(match)
      )
    ),
    R.when(
      R.always(filters.searchText.length > 0),
      R.filter(match =>
        R.pipe(
          R.map(
            R.pipe(
              R.defaultTo(""),
              R.trim,
              R.toLower
            )
          ),
          R.reject(R.isEmpty),
          R.any(R.includes(filters.searchText))
        )([
          match.fullname,
          `${match.first} ${match.last}`,
          match.nationalId,
          match.email,
          match.emailAddress
        ])
      )
    )
  )(matches);
}
